'use client'

import { CourierProvider } from '@trycourier/react-provider'
import { ReactNode } from 'react'

import { env } from '@/core/config/env'
import { useAppSelector } from '@/core/store/hooks'

interface ConfigNotificationsCourierProviderProps {
  children: ReactNode
}

/**
 * Monta `<CourierProvider>` (el que abre el WebSocket de Courier para push real-time).
 *
 * El provider siempre se monta para que el `<Inbox>` del árbol tenga contexto del store de Courier.
 * `nombreUsuario` (Redux) y `clientKey` (runtimeConfig) deberían estar hidratados antes de que
 * Header (que es donde se monta la campana) sea renderizado, porque el flujo es:
 *   login → MSAL completa → Redux setea usuario → layout monta → Header monta → este provider monta.
 *
 * Notas:
 * - NO usar `key={...}` para forzar remount cuando estos valores cambian — genera un loop
 *   "Maximum update depth exceeded" al remontarse en cascada con el `<Inbox>` hijo.
 * - NO consumir el store con `useInbox()` en paralelo al `<Inbox>` — dos consumidores del mismo
 *   store de Courier hacen que sus middlewares se retroalimenten y disparen el mismo loop.
 * - NO montar acá `<NotificationsUiList />`: la lista de avisos in-app la monta
 *   `ConfigNotificationProvider`, que es hijo de este provider. Montarla en los dos dibujaba
 *   cada aviso duplicado, con dos temporizadores de cierre independientes.
 */
const ConfigNotificationsCourierProvider: React.FC<ConfigNotificationsCourierProviderProps> = ({ children }) => {
  const { nombreUsuario } = useAppSelector((state) => state.UserReducer)
  const clientKey = env.NOTIFICATION_CLIENT_KEY

  return (
    <CourierProvider userId={nombreUsuario ?? ''} clientKey={clientKey}>
      {children}
    </CourierProvider>
  )
}

export default ConfigNotificationsCourierProvider
